/**
 * Responsive Design Breakpoints
 * Standardized breakpoints for consistent responsive behavior
 *
 * @module styles/breakpoints
 */

/**
 * Breakpoint values in pixels
 * Based on common device widths
 */
export const BREAKPOINTS = {
  // Mobile devices
  mobileSmall: '320px',   // iPhone SE, older Android
  mobile: '375px',        // iPhone 12/13 mini
  mobileLarge: '428px',   // iPhone 12/13 Pro Max

  // Tablets
  tablet: '768px',        // iPad, Android tablets (portrait)
  tabletLarge: '1024px',  // iPad Pro, Android tablets (landscape)

  // Desktop
  laptop: '1280px',       // Small laptops
  desktop: '1440px',      // Standard desktop
  desktopLarge: '1920px', // Large desktop/monitors
};

/**
 * Media query helpers for use in styled-components
 *
 * @example
 * const Button = styled.button`
 *   font-size: 14px;
 *
 *   ${media.tablet} {
 *     font-size: 16px;
 *   }
 * `;
 */
export const media = {
  // Mobile-first approach: min-width queries
  mobileSmall: `@media (min-width: ${BREAKPOINTS.mobileSmall})`,
  mobile: `@media (min-width: ${BREAKPOINTS.mobile})`,
  mobileLarge: `@media (min-width: ${BREAKPOINTS.mobileLarge})`,
  tablet: `@media (min-width: ${BREAKPOINTS.tablet})`,
  tabletLarge: `@media (min-width: ${BREAKPOINTS.tabletLarge})`,
  laptop: `@media (min-width: ${BREAKPOINTS.laptop})`,
  desktop: `@media (min-width: ${BREAKPOINTS.desktop})`,
  desktopLarge: `@media (min-width: ${BREAKPOINTS.desktopLarge})`,

  // Max-width queries for targeting specific ranges
  maxMobile: `@media (max-width: ${BREAKPOINTS.tablet})`,
  maxTablet: `@media (max-width: ${BREAKPOINTS.tabletLarge})`,
  maxLaptop: `@media (max-width: ${BREAKPOINTS.desktop})`,

  // Orientation queries
  portrait: '@media (orientation: portrait)',
  landscape: '@media (orientation: landscape)',

  // Touch device detection
  touch: '@media (hover: none) and (pointer: coarse)',
  mouse: '@media (hover: hover) and (pointer: fine)',

  // High DPI screens
  retina: '@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi)',
};

/**
 * Container max-widths for different breakpoints
 * Ensures content doesn't stretch too wide on large screens
 */
export const CONTAINER_MAX_WIDTHS = {
  mobile: '100%',
  tablet: '720px',
  laptop: '960px',
  desktop: '1140px',
  desktopLarge: '1320px',
};

/**
 * Responsive spacing scale
 * Use these instead of hard-coded values for consistency
 */
export const SPACING = {
  xs: '0.25rem',   // 4px
  sm: '0.5rem',    // 8px
  md: '1rem',      // 16px
  lg: '1.5rem',    // 24px
  xl: '2rem',      // 32px
  xxl: '3rem',     // 48px
  xxxl: '4rem',    // 64px
};

/**
 * Responsive font sizes
 * Scales appropriately across devices
 */
export const FONT_SIZES = {
  // Mobile-first base sizes
  xs: '0.75rem',    // 12px
  sm: '0.875rem',   // 14px
  base: '1rem',     // 16px
  lg: '1.125rem',   // 18px
  xl: '1.25rem',    // 20px
  '2xl': '1.5rem',  // 24px
  '3xl': '1.875rem',// 30px
  '4xl': '2.25rem', // 36px
  '5xl': '3rem',    // 48px
};

/**
 * Touch target sizes (minimum for accessibility)
 * iOS HIG: 44x44pt, Material Design: 48x48dp
 */
export const TOUCH_TARGET = {
  minimum: '44px',     // Absolute minimum
  comfortable: '48px', // Recommended
  large: '56px',       // For primary actions
};

/**
 * Z-index scale for layering
 * Prevents z-index conflicts
 */
export const Z_INDEX = {
  behind: -1,
  base: 0,
  dropdown: 100,
  sticky: 200,
  fixed: 300,
  modalBackdrop: 400,
  modal: 500,
  popover: 600,
  tooltip: 700,
  notification: 800,
  max: 9999,
};

/**
 * Utility function to create responsive value object
 *
 * @param {Object} values - Values for each breakpoint
 * @returns {string} CSS with media queries
 *
 * @example
 * const fontSize = responsive({
 *   mobile: '14px',
 *   tablet: '16px',
 *   desktop: '18px'
 * });
 */
export const responsive = (values) => {
  let css = '';

  if (values.mobile) css += `font-size: ${values.mobile};`;
  if (values.tablet) css += `${media.tablet} { font-size: ${values.tablet}; }`;
  if (values.laptop) css += `${media.laptop} { font-size: ${values.laptop}; }`;
  if (values.desktop) css += `${media.desktop} { font-size: ${values.desktop}; }`;

  return css;
};

/**
 * Common device dimensions for testing
 */
export const DEVICE_SIZES = {
  // Mobile phones (portrait)
  iPhoneSE: { width: 375, height: 667 },
  iPhone12: { width: 390, height: 844 },
  iPhone12ProMax: { width: 428, height: 926 },
  galaxyS20: { width: 360, height: 800 },
  pixel5: { width: 393, height: 851 },

  // Tablets (portrait)
  iPadMini: { width: 768, height: 1024 },
  iPadAir: { width: 820, height: 1180 },
  iPadPro11: { width: 834, height: 1194 },
  iPadPro12: { width: 1024, height: 1366 },

  // Desktop
  laptop: { width: 1366, height: 768 },
  desktop: { width: 1920, height: 1080 },
};

/**
 * Viewport height fix for mobile browsers
 * Use dvh (dynamic viewport height) when available
 * Fallback to vh for older browsers
 */
export const viewportHeight = (value) => `
  height: ${value}vh;
  height: ${value}dvh;
`;

export const minViewportHeight = (value) => `
  min-height: ${value}vh;
  min-height: ${value}dvh;
`;

export const maxViewportHeight = (value) => `
  max-height: ${value}vh;
  max-height: ${value}dvh;
`;

export default {
  BREAKPOINTS,
  media,
  CONTAINER_MAX_WIDTHS,
  SPACING,
  FONT_SIZES,
  TOUCH_TARGET,
  Z_INDEX,
  responsive,
  DEVICE_SIZES,
  viewportHeight,
  minViewportHeight,
  maxViewportHeight,
};
